import React, {
    forwardRef,
    MutableRefObject,
    useCallback,
    useRef,
    useState,useEffect
} from "react";
import {
    AddBeneficiary,
    ButtonBox,
    InputDatePicker, LoadingBox,
    ModelDialogBox,
    SelectBox,
    TextBox,
    UploadDialogWithHandlerWrapper,
} from "../..";
import {
    getLabelName,
    getOriginalValueInCurrency, handleSelectInputChange,
    isArabicCurrentLanguage,
    updateStateDynamically,
} from "../../../utils";
import {
    CustomerComponentDisplayModeEnum,
    CustomerResponseModel, DailyTransactionTypeEnum, HasFormIdModel,
    InputDatePickerTimeDisplayModeEnum,
    KeyValueStateModel,
    PageEnum,
    PaymentTypeEnum,
    RequestActionModel,
    SupplierCustomerTypeEnum,
    TextBoxVersionEnum,
    TransactionDetailResponseModel,
    TransactionItemResponseModel,
} from "../../../models";
import {
    getTextBoxNumericOrDefaultValue,
    getTextBoxValue,
    handleChangeTransactionDiscountRatio,
    handleSearchAndFilterBeneficiaryV2,
    initializeTransactionValuesAndUserTransactionsConfiguration
} from "../common/businessLogic/transactionBl";
import {InputActionMeta} from "react-select";
import {useFormikContext} from "formik";
import {
    useGuidKey,
    useTransactionItemObject,
    useTransactionObject, useTransactionResetKey,
    useTransactionUploadFiles
} from "../common/hooks/transactions/useTransactionSelectors";
import { useTransactionActions } from "../common/hooks/transactions/useTransactionActions";
import {
    customerInitialValue,
    transactionDetailInitialValues,
    userTransactionsSettingInitialValues
} from "../common/initialValues/initialValues";
import {useTransactionInitializationContext} from "../common/hooks/initializer/useTransactionInitializationContext";
import {
    useBeneficiaryList,
    useCalcTypeList,
    useCostCenterList,
    useCurrencyList,
    useIsSalesComponent,
    useItemList,
    usePaymentTypeList,
    useStoreEmployeeList
} from "../common/hooks/initializer/useTransactionInitializationSelectors";
import {useSystemConfigurationsContext} from "../common/hooks/configurations/useSystemConfigurationsContext";
import {isTransactionConfiguredForSavePurchaseFromTransaction} from "../common/businessLogic/validationBl";
import {useTransactionInitializationActions} from "../common/hooks/initializer/useTransactionInitializationActions";
import {
    handleBeneficiarySelectChange,
    handleCalcTypeSelectChange,
    handleCostCenterSelectChange,
    handleCurrencySelectChange,
    handleEmployeeSelectChange,
    handlePaymentTypeForSupplierSelectChange,
    handlePaymentTypeSelectChange
} from "../common/handlers/registerTransactionHeader.handler";
import {useAutoSync} from "../../../hooks/common/useAutoSync";
import {useGenericSystemConfigurations, useUserConfiguration} from "../../../hooks";
interface RegisterTransactionHeaderProps extends HasFormIdModel {
    transactionType: DailyTransactionTypeEnum;
    lookupRefs: Record<string, React.MutableRefObject<any>>;
}

const RegisterTransactionHeaderComponent= forwardRef(
    ({transactionType,lookupRefs,formId,}: RegisterTransactionHeaderProps,ref
    ) => {
        //#region state & hooks
        const { handleSetObject,handleSetTransactionItemObject,handleSetUploadedFiles,handleSetLoading} = useTransactionActions();
        const { setFieldValue,values, errors, handleBlur  } = useFormikContext<TransactionDetailResponseModel>();
        const {isLoaded:isTransactionInitializationLoaded}=useTransactionInitializationContext({transactionType,formId});
        const {isLoaded:isConfigurationLoaded}=useSystemConfigurationsContext({transactionType,formId});
        const {genericSystemConfigurations, isConfigurationLoaded: isGenericSystemConfigLoaded} = useGenericSystemConfigurations();
        const { userConfiguration: { userSetting, companySetting,userTransactionSetting }} = useUserConfiguration(transactionType, handleSetLoading);
        const request = useTransactionObject();
        const transactionItemObject=useTransactionItemObject();
        const isSalesComponent=useIsSalesComponent();
        const {handleSetBeneficiaryList}=useTransactionInitializationActions();
        const [localData, setLocalData] = useState<TransactionDetailResponseModel>(request ?? transactionDetailInitialValues);
        const [showAddBeneficiaryModel, setShowAddBeneficiaryModel] = useState(false);
        const [customerObject] = useState<CustomerResponseModel>(customerInitialValue);
        const uploadedFiles=useTransactionUploadFiles();
        const beneficiaryList=useBeneficiaryList();
        const originalBeneficiaryList=useBeneficiaryList();
        const itemList=useItemList();
        const paymentTypeList=usePaymentTypeList();
        const calcTypeList=useCalcTypeList();
        const currencyList=useCurrencyList();
        const costCenterList=useCostCenterList();
        const storeEmployeeList=useStoreEmployeeList();
        const datePickerInputRef = useRef<any>(null);
        const resetKey = useTransactionResetKey();
        const guidKey=useGuidKey();
        const { handleComponentBlur, componentRef: autoSyncRef } = useAutoSync({
            localData,
            onSync: async (data) => {
                const currentRequest =request ?? transactionDetailInitialValues;
                const merged: TransactionDetailResponseModel = {
                    ...currentRequest,
                    // Header fields you edit in this component:
                    Code: data.Code,
                    Note: data.Note,
                    Customer_ID: data.Customer_ID,
                    balance: data.balance,
                    CalcType_ID: data.CalcType_ID,
                    PaymentType_ID: data.PaymentType_ID,
                    Currency_ID: data.Currency_ID,
                    Emp_ID: data.Emp_ID,
                    CostCenter_ID: data.CostCenter_ID,
                    DiscountInCurrency: data.DiscountInCurrency,
                    Discount: data.Discount,
                    discountRatio: data.discountRatio,
                    Date: data.Date,
                    DueDate: data.DueDate,
                    PaymentTypeIdForSupplier: data.PaymentTypeIdForSupplier,
                    TotalMonyInCurrency: data.TotalMonyInCurrency,
                    TotalTaxInCurrency: data.TotalTaxInCurrency,
                    TotalNetInCurrency: data.TotalNetInCurrency,
                    // add/remove any header fields you want to sync from localData
                };
                handleSetObject(merged);

                await setFieldValue("Code", merged.Code);
                await setFieldValue("Note", merged.Note);
            },
            syncDelay: 1000, // Auto-sync after 1 second of no changes
            enableAutoSync: true
        });
        //#endregion
        //#region variables
        const discountPercentageMaxValue: number = 50;
        const {
            calcTypeSelectBoxMultiselectRef,
            paymentTypeSelectBoxMultiselectRef,
            currencySelectBoxMultiselectRef,
            storeEmployeeSelectBoxMultiselectRef,
            costCenterSelectBoxMultiselectRef,
            paymentTypeForSupplierPaymentSelectBoxMultiselectRef,
        } = lookupRefs;
        const canSavePurchaseTransactionFromSales = isTransactionConfiguredForSavePurchaseFromTransaction(transactionType,genericSystemConfigurations);
        //#endregion
        //#region functions
        const updateLocalData = useCallback((field: string, value: any | null) => {
            setLocalData((prev) => ({
                ...prev,
                [field]: value,
            }));
        }, []);
        const updateTransactionItemObjectState = async (
            key?: string | null,
            value?: any | null,
            keyValues?: KeyValueStateModel[] | null
        ) => {
            if (key && value !== undefined) {
                handleSetTransactionItemObject({
                    ...(transactionItemObject as TransactionItemResponseModel),
                    [key]: value,
                });
            }
            if (keyValues?.length) {
                const updatedObject = keyValues.reduce(
                    (acc, { key, value }) => {
                        if (key && Object.hasOwn(acc, key)) {
                            (acc as Record<string, any>)[key] = value;
                        }
                        return acc;
                    },
                    { ...transactionItemObject }
                );
                handleSetTransactionItemObject(updatedObject as TransactionItemResponseModel);
            }
        };
        const onCurrencySelectChange = useCallback(
            handleCurrencySelectChange(
                setLocalData,
                localData,
                transactionItemObject,
                beneficiaryList,
                itemList,
                currencyList,
                updateTransactionItemObjectState
            ),
            [
                setLocalData,
                localData,
                transactionItemObject,
                beneficiaryList,
                itemList,
                currencyList,
                updateTransactionItemObjectState
            ]
        );
        const onBeneficiarySelectChange = useCallback(
            handleBeneficiarySelectChange(setLocalData, localData, beneficiaryList),
            [setLocalData, localData, beneficiaryList]
        );
        const onCalcTypeSelectChange = useCallback(
            handleCalcTypeSelectChange(setLocalData, localData, transactionItemObject, itemList, userTransactionSetting),
            [setLocalData, localData, transactionItemObject, itemList, userTransactionSetting]
        );
        const onPaymentTypeSelectChange = useCallback(
            handlePaymentTypeSelectChange(setFieldValue, setLocalData, localData),
            [setFieldValue, setLocalData, localData]
        );
        const onEmployeeSelectChange = useCallback(
            handleEmployeeSelectChange(setLocalData, localData),
            [setLocalData, localData]
        );
        const onCostCenterSelectChange = useCallback(
            handleCostCenterSelectChange(setLocalData, localData),
            [setLocalData, localData]
        );
        const onPaymentTypeForSupplierSelectChange = useCallback(
            handlePaymentTypeForSupplierSelectChange(setLocalData, localData),
            [setLocalData, localData]
        );
        //#endregion
        //#region useEffect
        useEffect(() => {
            const initializeData = async () => {
                if( isTransactionInitializationLoaded && isConfigurationLoaded && ( localData.ID==null ||localData.ID==0)) {
                    await initializeTransactionValuesAndUserTransactionsConfiguration({
                        setTransactionDetail: setLocalData,
                        transactionDetail: localData,
                        currentTransactionSetting: userTransactionSetting || userTransactionsSettingInitialValues,
                        beneficiaryList: beneficiaryList,
                        paymentTypeList: paymentTypeList,
                        currencyList: currencyList,
                        storeEmployeeList: storeEmployeeList,
                        calcTypeList: calcTypeList,
                        costCenterList: costCenterList
                    });
                }
            }
            initializeData().then(() => {});
        }, [isTransactionInitializationLoaded,isConfigurationLoaded, localData.ID])
        useEffect(() => {
            const initializeData = async () => {
                if(resetKey!=0) {
                    setLocalData({...transactionDetailInitialValues});
                    await initializeTransactionValuesAndUserTransactionsConfiguration({
                        setTransactionDetail: setLocalData,
                        transactionDetail: transactionDetailInitialValues,
                        currentTransactionSetting: userTransactionSetting || userTransactionsSettingInitialValues,
                        beneficiaryList: beneficiaryList,
                        paymentTypeList: paymentTypeList,
                        currencyList: currencyList,
                        storeEmployeeList: storeEmployeeList,
                        calcTypeList: calcTypeList,
                        costCenterList: costCenterList
                    });
                }
            }
            initializeData().then(() => {});
        }, [resetKey])
        useEffect(() => {
            const initializeData = async () => {
                if (!guidKey || !request) return;
                setLocalData(request);
                await initializeTransactionValuesAndUserTransactionsConfiguration({
                    setTransactionDetail: setLocalData,
                    transactionDetail: request,
                    currentTransactionSetting: userTransactionSetting || userTransactionsSettingInitialValues,
                    beneficiaryList,
                    paymentTypeList,
                    currencyList,
                    storeEmployeeList,
                    calcTypeList,
                    costCenterList,
                });
            }
            initializeData().then(() => {});
        }, [guidKey])
        useEffect(() => {
            if (!request) return;
            if (request.ID != localData.ID) {
                setLocalData(request);
            }
        }, [request]);
        //#endregion
        //#region html
        if(!isTransactionInitializationLoaded || !isConfigurationLoaded){
            return <LoadingBox />;
        }
        return (
            <div
                ref={autoSyncRef}           // CHANGED: Use autoSyncRef from hook
                onBlur={handleComponentBlur} // CHANGED: Use handleComponentBlur from hook
            >
                <div className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4">
                    <div className="col-xl-4 col-lg-4">
                        <TextBox
                            labelName={getLabelName("Code")}
                            isReadOnly={userSetting?.EnableManualInvoiceCode ?? true}
                            inputName={"Code"}
                            errorText={errors.Code}
                            type="text"
                            inputValue={localData.Code}
                            onChange={(e: any) => {
                                const value = getTextBoxValue(e);
                                updateLocalData("Code", value);
                            }}
                            onBlur={handleBlur}
                        />
                    </div>
                    <div className="col-xl-8 col-lg-8 flex-form-control">
                        <SelectBox
                            id="customersSelectBox"
                            labelName={isSalesComponent
                                ? getLabelName("Customer")
                                : getLabelName("Supplier")
                            }
                            isSingleSelect={true}
                            selectedValues={
                                localData.Customer_ID != null
                                    ? [localData.Customer_ID.toString()]
                                    : []
                            }
                            source={beneficiaryList}
                            multiselectRef={lookupRefs.customerSelectBoxMultiselectRef}
                            onInputChange={(inputValue: string, actionMeta: InputActionMeta) => {
                                return handleSelectInputChange(inputValue, actionMeta, {
                                    localList: beneficiaryList||[],
                                    apiSearchFn: async (searchValue) => {
                                        await handleSearchAndFilterBeneficiaryV2({
                                            setState:handleSetBeneficiaryList,
                                            stateList:beneficiaryList,
                                            originalStateList:originalBeneficiaryList,
                                            beneficiaryType:isSalesComponent ? SupplierCustomerTypeEnum.Customer: SupplierCustomerTypeEnum.Supplier,
                                            searchValue: searchValue,
                                        });
                                    }
                                });
                            }}
                            onStatusChange={onBeneficiarySelectChange}
                            errorText={errors.Customer_ID}
                            controlSize={"w-100 w-calc"}
                        ></SelectBox>
                        <ButtonBox
                            variant=""
                            className="btn-gradient-primary btn-field-group"
                            onClick={() => {
                                setShowAddBeneficiaryModel(true);
                            }}
                        >
                            <span className="sr-only">Add</span>
                            <i className="mdi mdi-plus"></i>
                        </ButtonBox>
                    </div>
                    <ModelDialogBox
                        isXCloseButtonVisible={true}
                        isModelVisible={showAddBeneficiaryModel}
                        isCloseButtonVisible={false}
                        isEscapeCloseEnabled={true}
                        title={
                            isSalesComponent
                                ? getLabelName("Customers")
                                : getLabelName("Supplier")
                        }
                        size="xl"
                        onCloseEvent={() => {
                            setShowAddBeneficiaryModel(false);
                        }}
                    >
                        <AddBeneficiary
                            type={
                                isSalesComponent
                                    ? SupplierCustomerTypeEnum.Customer
                                    : SupplierCustomerTypeEnum.Supplier
                            }
                            request={customerObject}
                            displayMode={
                                CustomerComponentDisplayModeEnum.defaultMode
                            }
                            onActionEvent={async function (
                                o: RequestActionModel
                            ): Promise<void> {
                                if (o.request !== null && o.request !== undefined) {
                                    handleSetBeneficiaryList([
                                        ...beneficiaryList,
                                        {
                                            value: o.request.ID,
                                            name: o.request.Name_En,
                                            nameAr: o.request.Name,
                                            otherValue: {
                                                accountId: o.request.Account_ID,
                                            },
                                            isAdded: true,
                                        },
                                    ]);
                                    handleSetObject({...localData, Customer_ID: o.request.ID, balance: 0});
                                    // Force refresh of the customer SelectBox by clearing and resetting its value
                                    if (lookupRefs.customerSelectBoxMultiselectRef?.current) {
                                        lookupRefs.customerSelectBoxMultiselectRef.current.setValue({
                                            value: o.request.ID.toString(),
                                            label: isArabicCurrentLanguage() ? o.request.Name : o.request.Name_En
                                        });
                                    }
                                    setShowAddBeneficiaryModel(false);
                                }
                            }}
                            formId={isSalesComponent ? PageEnum.Customers : PageEnum.Suppliers}
                        />
                    </ModelDialogBox>
                    <div className="col-xl-4 col-lg-4">
                        <TextBox
                            labelName={getLabelName("Balance")}
                            inputValue={localData.balance}
                            isReadOnly={true}
                            inputName="balance"
                        />
                    </div>
                    {userTransactionSetting?.ShowCalcType && calcTypeList.length > 1 && (
                        <div className="col-xl-4 col-lg-4">
                            <SelectBox
                                id="calcTypesSelectBox"
                                labelName={getLabelName("Calc Type")}
                                isSingleSelect={true}
                                selectedValues={
                                    localData.CalcType_ID !== null && localData.CalcType_ID !== undefined
                                        ? [localData.CalcType_ID.toString()]
                                        : []
                                }
                                source={calcTypeList}
                                //@ts-ignore
                                multiselectRef={(node: MutableRefObject<any>) =>
                                    node ? (calcTypeSelectBoxMultiselectRef.current = node) : null
                                }
                                onStatusChange={onCalcTypeSelectChange}
                                errorText={errors.CalcType_ID}
                            />
                        </div>
                    )}
                    {userTransactionSetting?.ShowPaymentType && (
                        <div className="col-xl-4 col-lg-4">
                            <SelectBox
                                id="paymentTypesSelectBox"
                                labelName={getLabelName("Payment Type")}
                                isSingleSelect={true}
                                selectedValues={
                                    localData.PaymentType_ID !== null && localData.PaymentType_ID !== undefined
                                        ? [localData.PaymentType_ID.toString()]
                                        : []
                                }
                                source={paymentTypeList}
                                //@ts-ignore
                                multiselectRef={(node: MutableRefObject<any>) =>
                                    node ? (paymentTypeSelectBoxMultiselectRef.current = node) : null
                                }
                                onStatusChange={onPaymentTypeSelectChange}
                                errorText={errors.PaymentType_ID}
                            />
                        </div>
                    )}
                    {userTransactionSetting?.ShowCurrency && currencyList.length > 1 && (
                        <div className="col-xl-4 col-lg-4">
                            <SelectBox
                                id="currencySelectBox"
                                labelName={getLabelName("Currency")}
                                isSingleSelect={true}
                                selectedValues={
                                    localData.Currency_ID !== null && localData.Currency_ID !== undefined
                                        ? [localData.Currency_ID.toString()]
                                        : []
                                }
                                source={currencyList}
                                //@ts-ignore
                                multiselectRef={(node: MutableRefObject<any>) =>
                                    node ? (currencySelectBoxMultiselectRef.current = node) : null
                                }
                                onStatusChange={onCurrencySelectChange}
                                errorText={errors.Currency_ID}
                            />
                        </div>
                    )}
                    {userTransactionSetting?.ShowEmployeeResponsibile && storeEmployeeList.length > 1 && (
                        <div className="col-xl-4 col-lg-4">
                            <SelectBox
                                id="storeEmployeesSelectBox"
                                labelName={getLabelName("Employee")}
                                isSingleSelect={true}
                                selectedValues={
                                    localData.Emp_ID !== null && localData.Emp_ID !== undefined
                                        ? [localData.Emp_ID.toString()]
                                        : []
                                }
                                source={storeEmployeeList}
                                //@ts-ignore
                                multiselectRef={(node: MutableRefObject<any>) =>
                                    node
                                        ? (storeEmployeeSelectBoxMultiselectRef.current = node)
                                        : null
                                }
                                onStatusChange={onEmployeeSelectChange}
                                errorText={errors.Emp_ID}
                            />
                        </div>
                    )}
                    {userTransactionSetting?.ShowCostCenter && (
                        <div className="col-xl-4 col-lg-4">
                            <SelectBox
                                id="costCenterSelectBox"
                                labelName={getLabelName("CostCenter")}
                                isSingleSelect={true}
                                selectedValues={
                                    localData.CostCenter_ID !== null && localData.CostCenter_ID !== undefined
                                        ? [localData.CostCenter_ID.toString()]
                                        : []
                                }
                                source={costCenterList}
                                //@ts-ignore
                                multiselectRef={(node: MutableRefObject<any>) =>
                                    node ? (costCenterSelectBoxMultiselectRef.current = node) : null
                                }
                                onStatusChange={onCostCenterSelectChange}
                                errorText={errors.CostCenter_ID}
                            />
                        </div>
                    )}
                    {userTransactionSetting?.EnableDiscount && (
                        <div className="col-xl-4 col-lg-4">
                            <TextBox
                                labelName={getLabelName("Discount")}
                                isReadOnly={
                                    !userTransactionSetting?.EnableDiscount
                                }
                                type="price"
                                preventNegative={true}
                                inputName={"DiscountInCurrency"}
                                inputValue={localData.DiscountInCurrency}
                                onChange={async (e: any) => {
                                    const valueInCurrency = getTextBoxNumericOrDefaultValue(e);
                                    const value =
                                        valueInCurrency === 0
                                            ? 0
                                            : getOriginalValueInCurrency(
                                                valueInCurrency,
                                                localData.ValueCurrency
                                            );
                                    await updateStateDynamically(setLocalData, localData, null, null, [
                                        { key: "DiscountInCurrency", value: valueInCurrency },
                                        { key: "Discount", value: value }
                                    ]);
                                }}
                                onBlur={async (e: any) => {
                                   // const currentDiscount = getTextBoxNumericOrDefaultValue(e);
                                    //const _request=await calculateDistributedDiscountAfterTax(localData,{discountAmount: currentDiscount,discountPercentage:0})
                                    // setLocalData((prevData: TransactionDetailResponseModel) => ({
                                    //     ..._request,
                                    //     TransactionDetaillist: [..._request.TransactionDetaillist]
                                    // }));
                                }}
                            />
                        </div>
                    )}
                    {userTransactionSetting?.EnableDiscount &&
                        <div className="col-xl-4 col-lg-4">
                            <TextBox
                                labelName={getLabelName("Discount %")}
                                isReadOnly={!userTransactionSetting?.EnableDiscount}
                                type="price"
                                preventNegative={true}
                                inputName={"discountRatio"}
                                inputValue={localData.discountRatio}
                                onChange={async (e: any) => {
                                    let  value = getTextBoxNumericOrDefaultValue(e);
                                    value = Number(value) || 0;
                                    if (value > discountPercentageMaxValue) {
                                        value = discountPercentageMaxValue;
                                    }
                                    await updateStateDynamically(setLocalData, localData, null, null, [
                                        { key: "discountRatio", value: value },
                                    ]);
                                }}
                                onBlur={async () => {
                                    setLocalData((prev) => {
                                        handleChangeTransactionDiscountRatio(setLocalData, prev);
                                        return prev;
                                    });
                                }}
                            />
                        </div>
                    }
                    <div className="col-xl-4 col-lg-4">
                        <InputDatePicker
                            timeMode={InputDatePickerTimeDisplayModeEnum.None}
                            selectedDate={new Date(localData.Date.toString())}
                            className="form-control"
                            InputLabel={"Date"}
                            onChange={(date: Date) => {
                                setLocalData({
                                    ...localData,
                                    Date: date,
                                });
                            }}
                            maxDate={new Date()}
                            ref={datePickerInputRef}
                        />
                    </div>
                    {localData.PaymentType_ID == PaymentTypeEnum.Delay &&
                        <div className="col">
                            <InputDatePicker
                                selectedDate={localData.DueDate ? new Date(localData.DueDate.toString()) : undefined}
                                timeMode={InputDatePickerTimeDisplayModeEnum.None}
                                timeIntervals={15}
                                className="form-control"
                                InputLabel={"DueDate"}
                                onChange={(date: Date) => {
                                    setLocalData({
                                        ...localData,
                                        DueDate: date,
                                    });
                                }}
                                minDate={localData.minDueDate ? new Date(localData.minDueDate.toString()) : undefined}
                                ref={datePickerInputRef}
                            />
                        </div>
                    }
                    {canSavePurchaseTransactionFromSales && (
                        <div className="col-xl-4 col-lg-4">
                            <SelectBox
                                id="PaymentTypeForSupplierSelectBox"
                                labelName={getLabelName("Supplier Payment Type")}
                                isSingleSelect={true}
                                selectedValues={
                                    localData.PaymentTypeIdForSupplier != null
                                        ? [localData.PaymentTypeIdForSupplier.toString()]
                                        : []
                                }
                                source={paymentTypeList}
                                //@ts-ignore
                                multiselectRef={(node: MutableRefObject<any>) =>
                                    node ? (paymentTypeForSupplierPaymentSelectBoxMultiselectRef.current = node) : null
                                }
                                onStatusChange={onPaymentTypeForSupplierSelectChange}
                                errorText={errors.PaymentTypeIdForSupplier}
                            />
                        </div>
                    )}
                    <TextBox
                        inputName="totalMoneyInCurrency"
                        labelName={getLabelName("Total")}
                        inputValue={localData.TotalMonyInCurrency}
                        formatType={"decimal"}
                        isReadOnly={true}
                        isVisible={false}
                    />
                    {companySetting?.ApplyTax && (
                        <TextBox
                            labelName={getLabelName("Tax_Value")}
                            inputValue={localData.TotalTaxInCurrency}
                            inputName="taxValueInCurrency"
                            isReadOnly={true}
                            isVisible={false}
                        />
                    )}
                    <TextBox
                        inputName="netBillInCurrency"
                        labelName={getLabelName("Net Bill")}
                        inputValue={localData.TotalNetInCurrency}
                        isReadOnly={true}
                        isVisible={false}
                    />
                </div>
                <div className="row mt-4 row-cols-1 row-cols-xxl-12 row-cols-xl-12 row-cols-lg-12 row-cols-md-12 row-cols-sm-12 g-md-12 g-sm-12">
                    <TextBox
                        labelName={getLabelName("Notes")}
                        type="textarea"
                        inputName="Note"
                        inputValue={localData.Note}
                        onChange={async (e: any) => {
                            await updateStateDynamically(
                                setLocalData,
                                localData,
                                "Note",
                                e.target.value
                            );
                        }}
                        version={TextBoxVersionEnum.V2}
                    />
                </div>
                <UploadDialogWithHandlerWrapper
                    requestId={localData.ID}
                    entityDocumentType="Trasnaction"
                    handleSetUploadedFiles={handleSetUploadedFiles}
                    uploadedFiles={uploadedFiles}
                />
            </div>
        );
//#endregion
    });

export const    RegisterTransactionHeader = React.memo(
    RegisterTransactionHeaderComponent
);